/**
 * The club's schema.org identity, built from the settings columns that exist.
 *
 * OrganizationSchema and LocalBusinessSchema used to read business_email,
 * business_phone, business_address and social_*, none of which is a settings
 * column, so no club's phone, email, address or social profiles ever reached
 * search engines (2026-11-02). The real columns are contact_email,
 * contact_phone, contact_address (Settings -> Brand) and facebook_link,
 * instagram_link, x_link; see apps/backend/schema/columns.snapshot.json.
 *
 * Only what the club filled in is emitted: an empty or blank value leaves its
 * field out, and nothing is guessed. contact_address is free text, so it goes
 * out as text rather than being split into a PostalAddress it may not fit.
 */

export interface ClubSchemaSettings {
  business_name?: string | null;
  seo_default_description?: string | null;
  logo_url?: string | null;
  business_website?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  contact_address?: string | null;
  facebook_link?: string | null;
  instagram_link?: string | null;
  x_link?: string | null;
}

const filled = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v.trim() : null;

export function buildClubSchema(
  settings: ClubSchemaSettings | null | undefined,
  type: string | string[],
): Record<string, unknown> | null {
  const name = filled(settings?.business_name);
  if (!settings || !name) return null;

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": type,
    name,
  };
  const set = (key: string, value: unknown) => {
    const v = filled(value);
    if (v) schema[key] = v;
  };
  set("description", settings.seo_default_description);
  set("logo", settings.logo_url);
  set("email", settings.contact_email);
  set("telephone", settings.contact_phone);
  set("url", settings.business_website);
  set("address", settings.contact_address);

  const sameAs = [settings.facebook_link, settings.instagram_link, settings.x_link]
    .map(filled)
    .filter((v): v is string => Boolean(v));
  if (sameAs.length > 0) schema.sameAs = sameAs;

  return schema;
}
